/**
 * Types for AccountSetupBanner component.
 * These types mirror the backend API contract and should NOT be modified
 * without corresponding backend changes.
 */

import type { LucideIcon } from 'lucide-react';

/**
 * Backend API response shape for onboarding state.
 * Source: GET /api/user/onboarding-status
 */
export interface OnboardingState {
  shouldShowOnboardingBanner: boolean;
  completedSteps: string[];
  remainingSteps: OnboardingStep[];
  nextStep: OnboardingStep | null;
  totalSteps: number;
  completedCount: number;
  progressPercentage: number;
}

/**
 * Individual step metadata from backend.
 */
export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  isAccessible: boolean; // Backend determines if user can click this step
  order: number;
}

/**
 * Frontend-only metadata for rendering steps.
 * Maps step IDs to icons. This is the ONLY frontend-specific logic allowed.
 */
export const STEP_ICONS: Record<string, LucideIcon> = {
  email: require('lucide-react').Mail,
  'investment-account': require('lucide-react').Sparkles,
  bank: require('lucide-react').Building2,
  identity: require('lucide-react').Shield,
  // Investor-onboarding steps derived from the user object (see onboardingFromUser).
  'create-account': require('lucide-react').UserCheck,
  'schedule-meeting': require('lucide-react').CalendarDays,
  'verify-identity': require('lucide-react').ShieldCheck,
  'connect-funding': require('lucide-react').Landmark,
};

/**
 * Props for AccountSetupBanner component.
 */
export interface AccountSetupBannerProps {
  /** Onboarding state from backend. If null, component is loading. */
  onboarding: OnboardingState | null;

  /** Error message if onboarding fetch failed. */
  error?: string | null;

  /** Called when user clicks a step. Parent handles navigation. */
  onStepClick: (stepId: string) => void;

  /** Optional retry handler for when error occurs. */
  onRetry?: () => void;

  /** When false, the condensed sticky bar is not rendered (e.g. on detail pages). Default true. */
  stickyCondensed?: boolean;

  /** Measured height (px) of the sticky page header, supplied by the parent (which already
   *  measures it). Drives where the condensed sticky bar pins. Falls back to a default when
   *  omitted so the banner still renders standalone. */
  headerHeight?: number;

  /** Reports the measured height (px) of the condensed sticky bar so callers can pin other
   *  sticky content below it, instead of hard-coding a height. */
  onCondensedHeight?: (height: number) => void;

  /** localStorage key for the dismissed "You're all set" success bar. Pass a per-user key
   *  (e.g. `onboardingSuccessDismissed:${userId}`) so one user's dismissal doesn't hide the
   *  bar for a different account on the same browser. Defaults to a shared key. */
  dismissKey?: string;
}
